/**
 * Widget registry — maps server-defined widget.type names to Vue components.
 *
 * This is the first resolution phase in FieldResolver: if metaView.widget.type
 * matches a registered name here, that component is used. If the resolver
 * returns null, FieldResolver falls through to the type registry.
 *
 * Each resolver receives a context object and returns either:
 *   - A component factory: () => import('path/to/Component.vue')
 *   - null: to decline (e.g. AutoIncrement returns null in view mode,
 *     letting the type registry render it as a string)
 */
const widgets = new Map()

/**
 * Register a widget resolver.
 * @param {string} name - matches widget.type from server metadata (e.g. 'AutoIncrement')
 * @param {function} resolver - (context) => Component | null
 *   context: { page, editable, isEditMode, metaModel }
 */
export function registerWidget(name, resolver, defaultArgs = {}) {
  // import.meta.hot is Vite's HMR API — truthy during hot-reload. Suppress
  // the warning when HMR re-executes this module (re-registration is expected).
  if (widgets.has(name) && !import.meta.hot) {
    console.warn(`[widgetRegistry] Overwriting existing widget: ${name}`)
  }
  widgets.set(name, { resolver, defaultArgs })
}

/**
 * Resolve a widget by name.
 * @returns Component | null
 */
export function resolveWidget(name, context) {
  const entry = widgets.get(name)
  if (!entry) return null
  return entry.resolver(context)
}

/**
 * Check if a widget is registered (used by FieldResolver to determine registry hit).
 */
export function hasWidget(name) {
  return widgets.has(name)
}

/**
 * Return the default widget args declared at registration time.
 * These are merged into widgetArgs before UI overrides and server args.
 */
export function getWidgetDefaultArgs(name) {
  return widgets.get(name)?.defaultArgs ?? {}
}

/**
 * List all registered widget names (used by DevWidgetPlayground).
 */
export function getRegisteredWidgets() {
  return Array.from(widgets.keys())
}
